/**
 * PagosListPage (F85) — listado global de pagos con filtros y acción de anular.
 *
 * Filtros: rango de fechas, método, cajero (rol=cajero), incluir anulados.
 * Acciones: ver detalle, anular (con motivo obligatorio).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet, Ban, ExternalLink, AlertCircle, Printer, ListFilter, AlertTriangle, Info,
} from "lucide-react";
import { abrirReciboPago } from "@/features/config/api";
// rc139 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState } from "@/components/EmptyState";
import { formatMXN } from "@/lib/utils";
import type { ApiError } from "@/lib/api";

import { metodoPagoEnum, type MetodoPago, type Pago } from "./schemas";
import { useAnularPago, usePagos } from "./api";
import { useUsuarios } from "@/features/usuarios/api";
import { PagoDetalleModal, EstadoBadge, OrigenBadge } from "./PagoDetalleModal";
import { PagosExcepcionesSection } from "@/features/ajustes/PagosExcepcionesSection";

type Tab = "pagos" | "excepciones";

export function PagosListPage() {
  const [tab, setTab] = useState<Tab>("pagos");
  const [page, setPage] = useState(1);
  const [metodo, setMetodo] = useState<MetodoPago | "">("");
  const [origen, setOrigen] = useState<"" | "toku" | "saldo_a_favor" | "manual">("");
  const [registradoPor, setRegistradoPor] = useState<string>("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [incluirAnulados, setIncluirAnulados] = useState(false);
  const [detalleId, setDetalleId] = useState<string | null>(null);

  const { data: usersData } = useUsuarios({ page: 1, size: 200, activo: null });
  const users = usersData?.items ?? [];

  const filters = useMemo(() => ({
    page, size: 50, metodo, desde, hasta,
    registrado_por: registradoPor,
    incluir_anulados: incluirAnulados,
    payment_provider: origen,
  }), [page, metodo, desde, hasta, registradoPor, incluirAnulados, origen]);

  const { data, isLoading, isFetching } = usePagos(filters);
  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;
  const totalRango = items.reduce((s, p) => s + (p.anulado ? 0 : Number(p.monto)), 0);

  const [anulando, setAnulando] = useState<Pago | null>(null);
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Wallet size={16} /> Pagos
          </h2>
          <p className="text-xs text-muted-foreground max-md:hidden">
            Histórico global de pagos. Filtra por cajero, método y rango. Click en una fila para ver detalle.
          </p>
        </div>
        {tab === "pagos" && (
          <div className="text-right">
            <div className="text-[10px] uppercase text-muted-foreground">Total visible</div>
            <div className="text-lg font-semibold font-mono">{formatMXN(totalRango)}</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b">
        <TabBtn active={tab === "pagos"} onClick={() => setTab("pagos")}>
          <ListFilter size={12} /> Pagos
        </TabBtn>
        <TabBtn active={tab === "excepciones"} onClick={() => setTab("excepciones")}>
          <AlertTriangle size={12} /> Excepciones
        </TabBtn>
      </div>

      {tab === "excepciones" && (
        <div>
          <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-[11px] text-amber-700 dark:text-amber-300">
            <Info size={12} className="mt-0.5 shrink-0" />
            <span>
              Pagos Toku que llegaron sin poder aplicarse — cliente no migrado, factura inexistente
              o error de mapeo. Resolución manual desde aquí o desde Ajustes / Toku.
            </span>
          </div>
          <PagosExcepcionesSection />
        </div>
      )}

      {tab === "pagos" && (
      <>
      {/* Filtros */}
      {/* rc139 móvil: fechas a la mitad, selects a lo ancho */}
      <div className="flex flex-wrap items-end gap-2 rounded-lg border bg-card p-3 max-md:[&>div]:basis-full max-md:[&_select]:w-full max-md:[&_input[type=date]]:w-full">
        <div className="flex items-end gap-2 max-md:[&>div]:flex-1 max-md:[&>div]:min-w-0">
          <div>
            <Label className="text-[10px]">Desde</Label>
            <Input type="date" value={desde}
              onChange={(e) => { setDesde(e.target.value); setPage(1); }}
              className="text-xs" />
          </div>
          <div>
            <Label className="text-[10px]">Hasta</Label>
            <Input type="date" value={hasta}
              onChange={(e) => { setHasta(e.target.value); setPage(1); }}
              className="text-xs" />
          </div>
        </div>
        <div>
          <Label className="text-[10px]">Método</Label>
          <Select value={metodo}
            onChange={(e) => { setMetodo(e.target.value as MetodoPago | ""); setPage(1); }}
            className="text-xs">
            <option value="">Todos</option>
            {metodoPagoEnum.filter((m) => m !== "toku").map((m) => (
              <option key={m} value={m} className="capitalize">{m}</option>
            ))}
          </Select>
        </div>
        <div>
          <Label className="text-[10px]">Origen</Label>
          <Select value={origen}
            onChange={(e) => { setOrigen(e.target.value as typeof origen); setPage(1); }}
            className="text-xs">
            <option value="">Todos</option>
            <option value="toku">Toku</option>
            <option value="manual">Manual</option>
            <option value="saldo_a_favor">Saldo a favor</option>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Label className="text-[10px]">Cajero / admin</Label>
          <Select value={registradoPor}
            onChange={(e) => { setRegistradoPor(e.target.value); setPage(1); }}
            className="text-xs">
            <option value="">Todos</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre_completo || u.email} {u.rol_nombre && `(${u.rol_nombre})`}
              </option>
            ))}
          </Select>
        </div>
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={incluirAnulados}
            onChange={(e) => { setIncluirAnulados(e.target.checked); setPage(1); }} />
          Incluir anulados
        </label>
        {(desde || hasta || metodo || origen || registradoPor || incluirAnulados) && (
          <Button size="sm" variant="ghost"
            onClick={() => { setDesde(""); setHasta(""); setMetodo(""); setOrigen(""); setRegistradoPor(""); setIncluirAnulados(false); setPage(1); }}>
            Limpiar
          </Button>
        )}
      </div>

      {/* rc139 — móvil: tarjetas (toda la tarjeta abre el detalle) */}
      {isMobile ? (
        <>
          {isLoading && items.length === 0 ? (
            <MobileEmpty>Cargando…</MobileEmpty>
          ) : items.length === 0 ? (
            <MobileEmpty>Sin pagos con los filtros aplicados.</MobileEmpty>
          ) : (
            <MobileList>
              {items.map((p) => (
                <MobileCard
                  key={p.id}
                  onClick={() => setDetalleId(p.id)}
                  className={p.anulado ? "opacity-50" : ""}
                  title={p.cliente_nombre || "ver cliente"}
                  subtitle={<><span className="font-mono">{p.folio}</span> · {p.fecha}</>}
                  right={<span className="font-mono text-sm font-semibold">{formatMXN(Number(p.monto))}</span>}
                  actions={
                    !p.anulado ? (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => abrirReciboPago(p.id).catch((err) => toast.error("Error al abrir recibo", { description: err.message }))}>
                          <Printer size={13} /> Recibo
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setAnulando(p)}>
                          <Ban size={13} /> Anular
                        </Button>
                      </>
                    ) : undefined
                  }
                >
                  <div className="col-span-2 flex flex-wrap items-center gap-1.5">
                    <EstadoBadge estado={computeEstado(p)} />
                    <OrigenBadge origen={computeOrigenSimple(p)} />
                    {p.factura_id ? <span className="text-[11px] text-primary">con factura</span> : <span className="text-[11px] italic text-muted-foreground">crédito</span>}
                  </div>
                  <MobileMeta label="Cajero" span>{p.registrado_por_nombre || "—"}</MobileMeta>
                </MobileCard>
              ))}
            </MobileList>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 text-xs">
              <span className="text-muted-foreground">Pág {data?.page ?? 1}/{totalPages}{isFetching && " · actualizando…"}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </>
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Folio</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Factura</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Origen</TableHead>
              <TableHead>Cajero</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead className="text-right pr-4">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={Wallet}
                  title="Sin pagos"
                  description="Sin coincidencias con los filtros aplicados."
                />
              </TableEmpty>
            ) : (
              items.map((p) => (
                <TableRow
                  key={p.id}
                  className={`${p.anulado ? "opacity-50" : ""} cursor-pointer hover:bg-muted/40`}
                  onClick={() => setDetalleId(p.id)}
                >
                  <TableCell className="font-mono text-xs">{p.folio}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{p.fecha}</TableCell>
                  <TableCell>
                    {/* rc94: nombre real del cliente (antes decía "ver cliente").
                        Sigue siendo link a la ficha; fallback al texto viejo si
                        el backend aún no manda cliente_nombre (deploy a medias). */}
                    <Link
                      to={`/clientes/${p.cliente_id}`}
                      className="text-primary hover:underline text-xs block max-w-[240px] truncate"
                      title={p.cliente_nombre ?? undefined}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {p.cliente_nombre || "ver cliente"}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {p.factura_id ? (
                      <Link
                        to={`/facturas/${p.factura_id}`}
                        className="text-primary hover:underline text-xs flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        factura <ExternalLink size={10} />
                      </Link>
                    ) : (
                      <span className="text-[10px] text-muted-foreground italic">crédito</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <EstadoBadge estado={computeEstado(p)} />
                  </TableCell>
                  <TableCell>
                    <OrigenBadge origen={computeOrigenSimple(p)} />
                  </TableCell>
                  <TableCell className="text-xs">
                    {p.registrado_por_nombre || (
                      <span className="text-muted-foreground italic">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatMXN(Number(p.monto))}
                  </TableCell>
                  <TableCell className="text-right pr-4">
                    {!p.anulado && (
                      <>
                        <Button variant="ghost" size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirReciboPago(p.id).catch((err) =>
                              toast.error("Error al abrir recibo", { description: err.message })
                            );
                          }}
                          title="Imprimir recibo">
                          <Printer size={14} />
                        </Button>
                        <Button variant="ghost" size="icon"
                          onClick={(e) => { e.stopPropagation(); setAnulando(p); }}
                          title="Anular pago">
                          <Ban size={14} className="text-destructive" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">
              Página {data?.page ?? 1} de {totalPages}
              {isFetching && " · actualizando…"}
            </span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          </div>
        )}
      </div>
      )}
      </>
      )}

      <AnularPagoDialog
        pago={anulando}
        onClose={() => setAnulando(null)}
      />

      <PagoDetalleModal
        pagoId={detalleId}
        onClose={() => setDetalleId(null)}
      />
    </div>
  );
}

// ─── Tab button + helpers cálculo simple estado/origen ──────────────────
function TabBtn({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function computeEstado(p: Pago): "conciliado" | "saldo_a_favor" | "anulado" {
  if (p.anulado) return "anulado";
  if (!p.factura_id) return "saldo_a_favor";
  return "conciliado";
}

function computeOrigenSimple(p: Pago):
  | "toku_transfer" | "toku_card" | "toku_cash" | "toku_other" | "manual" | "saldo_a_favor" {
  // rc51: usar payment_provider (no metodo) — metodo es el método físico (efectivo/transferencia),
  // payment_provider es CÓMO entró al sistema (toku webhook vs cajero manual vs saldo a favor).
  const provider = (p.payment_provider || "").toLowerCase();
  if (provider === "toku") {
    // Sub-tipo más probable según metodo. Para detalle exacto, el modal pide /detalle.
    if (p.metodo === "transferencia") return "toku_transfer";
    if (p.metodo === "tarjeta") return "toku_card";
    if (p.metodo === "efectivo") return "toku_cash";
    return "toku_other";
  }
  if (provider === "saldo_a_favor") return "saldo_a_favor";
  return "manual";
}

// ─── Modal de anular pago ───────────────────────────────────────────────
function AnularPagoDialog({ pago, onClose }: { pago: Pago | null; onClose: () => void }) {
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mut = useAnularPago();

  // rc88 — Pago cobrado vía Toku NO es anulable (Toku no revierte el cobro).
  // El backend ya lo bloquea; aquí evitamos que el cajero teclee motivo y
  // pique "Confirmar" para toparse con el error: mostramos el aviso de una vez.
  const esToku = (pago?.payment_provider || "").toLowerCase() === "toku";

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (motivo.trim().length < 5) {
      setError("Motivo requerido (mínimo 5 caracteres)");
      return;
    }
    if (!pago) return;
    mut.mutate({ id: pago.id, motivo: motivo.trim() }, {
      onSuccess: () => {
        toast.success(`Pago ${pago.folio} anulado`);
        setMotivo("");
        onClose();
      },
      onError: (e) => setError((e as ApiError).message),
    });
  }

  return (
    <Dialog open={pago !== null} onOpenChange={(v) => { if (!v) { setMotivo(""); setError(null); onClose(); } }}>
      <form onSubmit={submit}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban size={16} className="text-destructive" /> Anular pago {pago?.folio}
            </DialogTitle>
            <DialogDescription>
              {esToku
                ? "Este pago fue cobrado mediante Toku."
                : "El pago se marca como anulado y la factura recupera el saldo. Auditado."}
            </DialogDescription>
          </DialogHeader>
          {esToku ? (
            <div className="flex items-start gap-1 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span>
                Este pago no se puede anular porque fue registrado mediante Toku.
                Un cobro ya procesado por Toku no puede revertirse.
              </span>
            </div>
          ) : (
            <>
              <div>
                <Label>Motivo *</Label>
                <Input
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Por qué se anula (mínimo 5 caracteres)"
                  autoFocus
                />
              </div>
              {error && (
                <div className="mt-2 flex items-start gap-1 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                  <AlertCircle size={12} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              {esToku ? "Cerrar" : "Cancelar"}
            </Button>
            {!esToku && (
              <Button type="submit" size="sm" variant="destructive" disabled={mut.isPending}>
                {mut.isPending ? "Anulando…" : "Confirmar anulación"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </form>
    </Dialog>
  );
}
